"use client";

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { formatDate } from "@/lib/utils";
import { Vehicle, Customer, Appointment } from "@/types";

interface VehicleDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vehicle: Vehicle | null;
  customers: Customer[];
  appointments: Appointment[];
}

export function VehicleDetailDialog({
  open,
  onOpenChange,
  vehicle,
  customers,
  appointments,
}: VehicleDetailDialogProps) {
  if (!vehicle) return null;

  const customer = customers.find((c) => c.id === vehicle.customerId);
  const vehicleAppointments = appointments.filter(
    (apt) => apt.vehicleId === vehicle.id
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl overflow-y-auto max-h-[calc(100vh-10rem)]">
        <DialogHeader>
          <DialogTitle>
            {vehicle.make} {vehicle.model}
          </DialogTitle>
          <DialogDescription>
            Vehicle details and service history
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          {/* Vehicle Info */}
          <Card>
            <CardHeader>
              <CardTitle>Vehicle Information</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Plate Number</Label>
                  <p className="font-medium">{vehicle.plateNumber}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Owner</Label>
                  <p>{customer?.name || "Unknown"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Make & Model</Label>
                  <p>
                    {vehicle.make} {vehicle.model}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Year</Label>
                  <p>{vehicle.year || "N/A"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Color</Label>
                  <div className="flex items-center gap-2">
                    <div
                      className="h-4 w-4 rounded-full border"
                      style={{
                        backgroundColor: vehicle.color.toLowerCase(),
                      }}
                    />
                    <p>{vehicle.color}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Type</Label>
                  <p className="capitalize">{vehicle.type}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Statistics */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">
                  {vehicle.totalServices}
                </div>
                <p className="text-xs text-muted-foreground">Total Services</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">
                  {vehicle.lastService
                    ? formatDate(vehicle.lastService)
                    : "Never"}
                </div>
                <p className="text-xs text-muted-foreground">Last Service</p>
              </CardContent>
            </Card>
          </div>

          {/* Service History */}
          <Card>
            <CardHeader>
              <CardTitle>Service History</CardTitle>
            </CardHeader>
            <CardContent>
              {vehicleAppointments.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No service history
                </p>
              ) : (
                <div className="space-y-2">
                  {vehicleAppointments.slice(0, 5).map((appointment) => (
                    <div
                      key={appointment.id}
                      className="flex items-center justify-between border rounded-lg p-3"
                    >
                      <div>
                        <p className="font-medium">{appointment.type.replace(/_/g, " ")}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(appointment.date)}
                        </p>
                      </div>
                      <Badge variant="outline">{appointment.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}

